import React from "react";
import type { Point, Rect } from "../types";

export interface GuideBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface GuideDistance {
  from: Point;
  to: Point;
  value: number;
  axis: "horizontal" | "vertical";
}

export interface SnapGuides {
  x?: number;
  y?: number;
  targetDot?: { x?: number; y?: number };
  /** Container the active shape is centred inside (both axes on centres). */
  centeredIn?: GuideBounds;
  /** The active shape at the snapped position, for the centred cue. */
  activeBounds?: GuideBounds;
  /** Edge-to-edge gaps to the nearest neighbours (Alt held). */
  distances?: GuideDistance[];
}

export type DraftShape =
  | { kind: "rect" | "ellipse" | "zone"; rect: Rect }
  | { kind: "wall"; from: Point; to: Point; thickness: number }
  | { kind: "line"; from: Point; to: Point }
  | { kind: "measure"; from: Point; to: Point; pixelsPerMeter: number | null }
  | {
      kind: "polyline";
      points: Point[];
      to: Point;
      color: string;
      /** The segment is locked to a 45° multiple (drawn solid). */
      isConstrained?: boolean;
      /** The next click closes the path at this vertex. */
      closeAt?: Point;
    }
  | { kind: "freehand"; points: Point[]; color: string };

/** Tool preview that follows the pointer (spot, icon or furniture). */
export interface GhostShape {
  kind: "spot" | "icon" | "furniture";
  x: number;
  y: number;
}

/** Numbers shown next to the pointer during a gesture: size while
 *  resizing, offset while moving, angle while rotating, length while
 *  drawing a wall. */
export interface Readout {
  /** World point the label hangs off (the pointer, usually). */
  anchor: Point;
  lines: string[];
}

export interface OverlayState {
  /** Shape under the pointer (select tool, nothing pressed). */
  hoverId: string | null;
  readout: Readout | null;
  marquee: Rect | null;
  guides: SnapGuides | null;
  draft: DraftShape | null;
  ghost: GhostShape | null;
  /** Scratch transform applied to the selection outline during a drag. */
  selectionTransform: string | null;
}

const EMPTY: OverlayState = {
  hoverId: null,
  readout: null,
  marquee: null,
  guides: null,
  draft: null,
  ghost: null,
  selectionTransform: null
};

/**
 * Gesture feedback that must not go through the store: tools write here at
 * most once per frame and only the overlay/draft components re-render.
 */
export interface OverlayChannel {
  get: () => OverlayState;
  set: (patch: Partial<OverlayState>) => void;
  reset: () => void;
  subscribe: (listener: () => void) => () => void;
}

export const createOverlayChannel = (): OverlayChannel => {
  let state = EMPTY;
  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) listener();
  };
  return {
    get: () => state,
    set: (patch) => {
      state = { ...state, ...patch };
      notify();
    },
    reset: () => {
      if (state === EMPTY) return;
      state = EMPTY;
      notify();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };
};

const OverlayChannelContext = React.createContext<OverlayChannel | null>(null);

export const OverlayChannelProvider = OverlayChannelContext.Provider;

export const useOverlayChannel = (): OverlayChannel => {
  const channel = React.useContext(OverlayChannelContext);
  if (!channel) {
    throw new Error("useOverlayChannel must be used within the map canvas");
  }
  return channel;
};

export const useOverlayValue = <T>(selector: (state: OverlayState) => T): T => {
  const channel = useOverlayChannel();
  return React.useSyncExternalStore(
    channel.subscribe,
    () => selector(channel.get()),
    () => selector(channel.get())
  );
};
